/**
 * Session context — holds the current authenticated user + the bearer
 * token used for /internal/* API calls. Persisted in sessionStorage so
 * hard-refreshes don't kick the user back to the SSO flow.
 *
 * Sprint 8 stubs the OIDC + TOTP flow — the SignIn page calls a mock
 * `exchange` endpoint that returns a pre-signed JWT. Real Google OIDC
 * lands in Sprint 9 alongside the mutation API.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode, type JSX } from 'react';
import { UNAUTHORIZED_EVENT } from './api';

export interface SessionUser {
  user_id: string;
  email: string;
  role: 'owner' | 'admin' | 'support' | 'readonly';
  mfa_ok: boolean;
}

export interface SessionState {
  user: SessionUser | null;
  token: string | null;
}

export interface SessionContextValue {
  state: SessionState;
  signIn(user: SessionUser, token: string): void;
  signOut(): void;
}

const Ctx = createContext<SessionContextValue | null>(null);

const STORAGE_KEY = 'vloud.admin.session';
/** Set when sign-out could not be confirmed by the server; read once by the sign-in page. */
export const LOGOUT_UNCONFIRMED_KEY = 'vloud.admin.logout_unconfirmed';

function loadInitial(): SessionState {
  if (typeof sessionStorage === 'undefined') return { user: null, token: null };
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return { user: null, token: null };
  try { return JSON.parse(raw) as SessionState; } catch { return { user: null, token: null }; }
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<SessionState>(loadInitial);

  const signIn = useCallback((user: SessionUser, token: string) => {
    const next = { user, token };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setState(next);
  }, []);

  const signOut = useCallback(() => {
    // Revoke the session on the server (F-11), not just forget it here: a bearer token copied
    // before logout must stop working. Raw fetch on purpose — the API client dispatches
    // UNAUTHORIZED_EVENT on a 401, which calls signOut again. Local state is always
    // cleared; a server failure is retried once and then surfaced on the sign-in page.
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const token = raw ? (JSON.parse(raw) as SessionState).token : null;
      if (token) {
        const base = (import.meta.env.VITE_LICENSE_SERVER_URL as string | undefined) ?? '';
        const attempt = (): Promise<boolean> => fetch(`${base}/admin/logout`, {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: '{}',
          keepalive: true,
        }).then((r) => r.ok || r.status === 401).catch(() => false); // 401 = already revoked/expired
        // One retry; if the server still cannot be reached the sign-in page says so, because the
        // bearer may stay valid until it expires.
        void attempt().then((ok) => ok ? true : new Promise<boolean>((res) => setTimeout(() => void attempt().then(res), 1000)))
          .then((ok) => { if (!ok) { try { sessionStorage.setItem(LOGOUT_UNCONFIRMED_KEY, '1'); } catch { /* ignore */ } } });
      }
    } catch { /* storage unavailable: nothing to revoke */ }
    sessionStorage.removeItem(STORAGE_KEY);
    setState({ user: null, token: null });
  }, []);

  // Any 401 from the server (token expired, revoked, secret rotated)
  // → clear the session. The ProtectedLayout then redirects to /signin.
  useEffect(() => {
    const handler = (): void => signOut();
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, [signOut]);

  return (
    <Ctx.Provider value={{ state, signIn, signOut }}>{children}</Ctx.Provider>
  );
}

export function useSession(): SessionContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
